import React, { useEffect, useMemo, useRef, useState } from "react";
import { InputNumber, Modal, Spin, Typography } from "antd";
import { TrainingAPI } from "@api/TrainingAPI";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import {
    extractQueryCalls,
    QueryCall,
    splitOperandsByRole,
    substituteQueryCalls,
    toQuotedFormula,
} from "./utils";

const { Text } = Typography;

const trainingAPI = new TrainingAPI();

const DEBOUNCE_MS = 400;

export interface FormulaTestModalProps {
    open: boolean;
    onClose: () => void;
    formula: string;
    operands: string[];
    operandLabels: Record<string, string>;
    decimalPlaces?: number | null;
}

const DEFAULT_DECIMALS = 2;

// Mirrors the backend's _SAMPLE_DAYS_SINCE, so opening the modal shows the same day
// count the formula validator has always assumed.
const DEFAULT_DAYS_SINCE = 3;

// Mirrors the backend's _SAMPLE_COUNT_TRUE.
const DEFAULT_COUNT_TRUE = 2;

// Mirrors the backend's _SAMPLE_VALUE_TO_HIT_ACWR.
const DEFAULT_VALUE_TO_HIT_ACWR = 100;

const DEFAULT_QUERY_CALL_VALUE: Record<QueryCall["fn"], number> = {
    days_since: DEFAULT_DAYS_SINCE,
    count_true: DEFAULT_COUNT_TRUE,
    value_to_hit_acwr: DEFAULT_VALUE_TO_HIT_ACWR,
};

// Round a numeric result to the metric's configured decimal places so the preview
// matches how the metric will display. Non-numeric results are shown as-is.
const formatResult = (value: number | string, decimalPlaces?: number | null): string => {
    if (typeof value !== "number" || !Number.isFinite(value)) return String(value);
    return value.toFixed(decimalPlaces ?? DEFAULT_DECIMALS);
};

type ResultState =
    | { status: "idle" }
    | { status: "loading" }
    | { status: "ok"; value: number | string }
    | { status: "invalid"; message: string }
    | { status: "error" };

// Turn a backend error_message into something a non-technical user can act on.
const friendlyError = (raw?: string): string => {
    const msg = raw ?? "";
    if (/division by zero|divide by zero/i.test(msg)) {
        return "Can't calculate — that produces a division by zero. Try different values.";
    }
    return "Can't calculate with these values — double-check them and try again.";
};

// Render the formula with bare operand tokens replaced by their human labels so
// the read-only preview is legible (e.g. `op1 - op2` → `Pre-Train Wt - Post-Train Wt`).
const toLabeledFormula = (formula: string, operandLabels: Record<string, string>): string =>
    formula.replace(/\bop\d+\b/g, (op) => operandLabels[op] ?? op);

const FormulaTestModal: React.FC<FormulaTestModalProps> = ({
    open,
    onClose,
    formula,
    operands,
    operandLabels,
    decimalPlaces,
}) => {
    const [values, setValues] = useState<Record<string, number | null>>({});
    const [dayCounts, setDayCounts] = useState<(number | null)[]>([]);
    const [result, setResult] = useState<ResultState>({ status: "idle" });
    const requestSeq = useRef(0);

    const queryCalls = useMemo(() => extractQueryCalls(formula), [formula]);

    // Operands referenced only inside a query condition cannot move the result —
    // the call resolves to a count before the surrounding arithmetic runs — so the
    // modal asks for that count instead of for their values.
    const { valueOperands } = useMemo(
        () => splitOperandsByRole(formula, operands),
        [formula, operands],
    );

    // Seed each value operand to 1 and each query call to the backend's sample count
    // whenever the modal opens (or the formula behind it changes).
    useEffect(() => {
        if (!open) return;
        setValues(valueOperands.reduce((acc, op) => ({ ...acc, [op]: 1 }), {}));
        setDayCounts(queryCalls.map((call) => DEFAULT_QUERY_CALL_VALUE[call.fn]));
        setResult({ status: "idle" });
    }, [open, valueOperands, queryCalls]);

    const labeledFormula = useMemo(
        () => toLabeledFormula(formula, operandLabels),
        [formula, operandLabels],
    );

    // The inputs are grouped per function, but `dayCounts` stays positional over
    // every query call, so each row keeps its original index.
    const indexedCalls = useMemo(
        () => queryCalls.map((call, index) => ({ call, index })),
        [queryCalls],
    );
    const daysSinceCalls = indexedCalls.filter(({ call }) => call.fn === "days_since");
    const countTrueCalls = indexedCalls.filter(({ call }) => call.fn === "count_true");
    const valueToHitCalls = indexedCalls.filter(({ call }) => call.fn === "value_to_hit_acwr");

    const conditionNote =
        daysSinceCalls.length > 0 && countTrueCalls.length > 0
            ? "days_since and count_true are simulated here — set a count for each condition."
            : countTrueCalls.length > 0
                ? "count_true is simulated here — set how many days the condition was true."
                : daysSinceCalls.length > 0
                    ? "days_since is simulated here — set how many days ago the condition was last true."
                    : "";
    const valueToHitNote = valueToHitCalls.length > 0
        ? "value_to_hit_acwr is simulated here — set a metric value in the metric's own unit. 0 means the ACWR stays at or above the target even with no load today."
        : "";
    const simulationNote = [conditionNote, valueToHitNote].filter(Boolean).join(" ");

    const inputCount = valueOperands.length + queryCalls.length;
    const allFilled =
        inputCount > 0 &&
        valueOperands.every((op) => typeof values[op] === "number") &&
        queryCalls.every((_, i) => typeof dayCounts[i] === "number");

    // Debounced auto-compute: recompute whenever the values change and every operand
    // has a numeric value. A monotonic sequence number discards out-of-order responses.
    useEffect(() => {
        if (!open || !allFilled) {
            setResult((prev) => (prev.status === "loading" ? { status: "idle" } : prev));
            return;
        }
        const timer = setTimeout(async () => {
            const seq = ++requestSeq.current;
            setResult({ status: "loading" });
            const sampleData = valueOperands.reduce(
                (acc, op) => ({ ...acc, [op]: values[op] }),
                {} as Record<string, number | null>,
            );
            // Substituting the day counts first leaves a formula of plain operands, which
            // the backend evaluates with the sample values above. The condition strings
            // themselves are still validated by the builder's own formula check.
            const testFormula = substituteQueryCalls(formula, dayCounts as number[]);
            try {
                const res: any = await trainingAPI.validateFormula(
                    toQuotedFormula(testFormula),
                    sampleData,
                );
                if (seq !== requestSeq.current) return;
                if (res?.is_valid && res?.detail?.result != null) {
                    setResult({ status: "ok", value: res.detail.result });
                } else {
                    setResult({ status: "invalid", message: friendlyError(res?.error_message) });
                }
            } catch (e) {
                if (seq !== requestSeq.current) return;
                message.error("Something went wrong while testing your formula.");
                setResult({ status: "error" });
            }
        }, DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [open, allFilled, values, dayCounts, formula, valueOperands]);

    return (
        <Modal
            title="Test your formula"
            open={open}
            onCancel={onClose}
            footer={null}
            destroyOnClose
            className="udm-test-modal"
        >
            <div className="udm-test-formula">
                <Text type="secondary">Formula</Text>
                <div className="udm-test-formula-value">{labeledFormula}</div>
                {queryCalls.length > 0 && (
                    <Text type="secondary" className="udm-test-date-note">
                        {simulationNote} The real metric reads your data day by day.
                    </Text>
                )}
            </div>

            <div className="udm-test-groups">
                {valueOperands.length > 0 && (
                    <div className="udm-test-group">
                        {queryCalls.length > 0 && (
                            <div className="udm-test-group-label">Values</div>
                        )}
                        <div className="udm-test-inputs">
                            {valueOperands.map((op) => (
                                <div key={op} className="udm-test-input-row">
                                    <Text className="udm-test-input-label">
                                        {operandLabels[op] ?? op}
                                    </Text>
                                    <InputNumber
                                        value={values[op] ?? null}
                                        onChange={(v) =>
                                            setValues((prev) => ({ ...prev, [op]: v }))
                                        }
                                        style={{ width: 120 }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {daysSinceCalls.length > 0 && (
                    <div className="udm-test-group">
                        <div className="udm-test-group-label">Days since</div>
                        <div className="udm-test-inputs">
                            {daysSinceCalls.map(({ call, index }) => (
                                <div key={call.start} className="udm-test-input-row">
                                    <Text className="udm-test-input-label udm-test-condition-label">
                                        {toLabeledFormula(call.condition, operandLabels)}
                                    </Text>
                                    <InputNumber
                                        value={dayCounts[index] ?? null}
                                        min={0}
                                        max={call.lookback}
                                        precision={0}
                                        onChange={(v) =>
                                            setDayCounts((prev) => {
                                                const next = [...prev];
                                                next[index] = v;
                                                return next;
                                            })
                                        }
                                        style={{ width: 120 }}
                                    />
                                </div>
                            ))}
                        </div>
                        <Text type="secondary" className="udm-test-group-hint">
                            {daysSinceCalls.length === 1
                                ? `0 = true today · up to ${daysSinceCalls[0].call.lookback} days`
                                : "0 = true today · each count is capped at its own lookback"}
                        </Text>
                    </div>
                )}

                {countTrueCalls.length > 0 && (
                    <div className="udm-test-group">
                        <div className="udm-test-group-label">Times true</div>
                        <div className="udm-test-inputs">
                            {countTrueCalls.map(({ call, index }) => (
                                <div key={call.start} className="udm-test-input-row">
                                    <Text className="udm-test-input-label udm-test-condition-label">
                                        {toLabeledFormula(call.condition, operandLabels)}
                                    </Text>
                                    <InputNumber
                                        value={dayCounts[index] ?? null}
                                        min={0}
                                        max={call.lookback + 1}
                                        precision={0}
                                        onChange={(v) =>
                                            setDayCounts((prev) => {
                                                const next = [...prev];
                                                next[index] = v;
                                                return next;
                                            })
                                        }
                                        style={{ width: 120 }}
                                    />
                                </div>
                            ))}
                        </div>
                        <Text type="secondary" className="udm-test-group-hint">
                            {countTrueCalls.length === 1
                                ? `0 = never true · up to ${countTrueCalls[0].call.lookback + 1} days can count`
                                : "0 = never true · each count is capped at its own window"}
                        </Text>
                    </div>
                )}

                {valueToHitCalls.length > 0 && (
                    <div className="udm-test-group">
                        <div className="udm-test-group-label">Value to hit ACWR</div>
                        <div className="udm-test-inputs">
                            {valueToHitCalls.map(({ call, index }) => (
                                <div key={call.start} className="udm-test-input-row">
                                    <Text className="udm-test-input-label udm-test-condition-label">
                                        {`${toLabeledFormula(call.condition, operandLabels)} → ACWR ${call.target}`}
                                    </Text>
                                    <InputNumber
                                        value={dayCounts[index] ?? null}
                                        min={0}
                                        onChange={(v) =>
                                            setDayCounts((prev) => {
                                                const next = [...prev];
                                                next[index] = v;
                                                return next;
                                            })
                                        }
                                        style={{ width: 120 }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="udm-test-result">
                <Text strong>Result:</Text>
                <span className="udm-test-result-value">
                    {result.status === "loading" && <Spin size="small" />}
                    {result.status === "ok" && (
                        <Text strong>{formatResult(result.value, decimalPlaces)}</Text>
                    )}
                    {result.status === "invalid" && <Text type="warning">{result.message}</Text>}
                    {result.status === "error" && (
                        <Text type="danger">Couldn't calculate. Please try again.</Text>
                    )}
                    {result.status === "idle" && <Text type="secondary">—</Text>}
                </span>
            </div>
        </Modal>
    );
};

export default FormulaTestModal;
